import React, { useCallback, useState } from "react";

import { BatchStock, RecipeStock, stockApi } from "@/client/api";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorMessage,
  FormField,
  Input,
  PageHeader,
  TopLoadingBar,
} from "@/client/components/ui";
import { useDataVersion } from "@/client/context/DataVersionContext";
import { useToast } from "@/client/context/ToastContext";
import { useApi } from "@/client/hooks/useApi";
import { FreezerIcon, StockIcon } from "@/client/icons";
import { daysUntil, urgencyColor, urgencyRowClass } from "@/client/utils/formatting";
import { toDateStr } from "@/shared/date";

// ─── Consume form ──────────────────────────────────────────────────────────────

function ConsumeForm({
  batches,
  recipeName,
  onClose,
  onDone,
}: {
  batches: BatchStock[];
  recipeName: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { toast } = useToast();
  const [batchId, setBatchId] = useState(batches[0]?.batch_id ?? "");
  const [servings, setServings] = useState("1");
  const [date, setDate] = useState(toDateStr(new Date()));
  const [saving, setSaving] = useState(false);

  async function handleSubmit() {
    const n = parseFloat(servings);
    if (!batchId || isNaN(n) || n <= 0) return;
    setSaving(true);
    try {
      await stockApi.consume({ batch_id: batchId, servings_consumed: n, consumption_date: date });
      toast(`${n} ${n !== 1 ? "porciones registradas" : "porción registrada"}`);
      onDone();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Error", "error");
      setSaving(false);
    }
  }

  return (
    <div className="mt-3 bg-term-raised border border-term-border rounded-xl p-4 flex flex-col gap-3">
      <div className="text-sm font-medium text-term-text">Registrar consumo — {recipeName}</div>

      {batches.length > 1 && (
        <FormField label="Lote">
          <select
            value={batchId}
            onChange={(e) => setBatchId(e.target.value)}
            className="w-full px-3 py-2 text-sm bg-term-bg border border-term-border rounded-lg text-term-text focus:border-term-accent-dim focus:outline-none"
          >
            {batches.map((b) => (
              <option key={b.batch_id} value={b.batch_id}>
                {b.prep_date} — {b.servings_remaining} porciones restantes
              </option>
            ))}
          </select>
        </FormField>
      )}

      <div className="grid grid-cols-2 gap-3">
        <FormField label="Porciones">
          <Input value={servings} onChange={setServings} type="number" placeholder="1" />
        </FormField>
        <FormField label="Fecha">
          <Input value={date} onChange={setDate} type="date" />
        </FormField>
      </div>

      <div className="flex gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleSubmit} disabled={saving}>
          {saving ? "Guardando..." : "Registrar"}
        </Button>
      </div>
    </div>
  );
}

// ─── Batch rows (expanded) ─────────────────────────────────────────────────────

function BatchRows({ batches }: { batches: BatchStock[] }) {
  return (
    <div className="pl-4 border-l-2 border-term-border ml-2 mt-1 flex flex-col gap-1">
      {batches.map((b) => {
        const expiryDays = daysUntil(b.expiry_date);
        return (
          <div
            key={b.batch_id}
            className="flex items-center gap-3 text-sm py-1.5 text-term-text-dim flex-wrap"
          >
            <span className="shrink-0">{b.prep_date}</span>
            {b.variant_name && (
              <span className="shrink-0 text-micro font-medium text-term-accent bg-term-accent/10 px-1.5 py-0.5 rounded-full border border-term-accent/20">
                {b.variant_name}
              </span>
            )}
            <span className="shrink-0">
              {b.servings_remaining} / {b.servings_produced} porc.
            </span>
            <span className="inline-flex items-center gap-1 shrink-0">
              {b.storage_location === "freezer" ? (
                <FreezerIcon
                  width={14}
                  height={14}
                  className="shrink-0 text-term-text-muted"
                  aria-hidden
                />
              ) : (
                <StockIcon
                  width={14}
                  height={14}
                  className="shrink-0 text-term-text-muted"
                  aria-hidden
                />
              )}
              {b.storage_location === "freezer" ? "freezer" : "heladera"}
            </span>
            {expiryDays !== null && (
              <span className={`shrink-0 ${urgencyColor(expiryDays)}`}>
                {expiryDays <= 0 ? "vencido" : `vence en ${expiryDays}d`}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Recipe row ────────────────────────────────────────────────────────────────

function RecipeRow({
  recipe,
  onConsumed,
  initialExpanded,
  index,
}: {
  recipe: RecipeStock;
  onConsumed: () => void;
  initialExpanded?: boolean;
  index?: number;
}) {
  const [expanded, setExpanded] = useState(!!initialExpanded);
  const [showConsume, setShowConsume] = useState(false);
  const urgentBatch = recipe.batches.reduce<number | null>((min, b) => {
    const d = daysUntil(b.expiry_date);
    if (d === null) return min;
    return min === null ? d : Math.min(min, d);
  }, null);

  return (
    <Card className={`overflow-hidden ${urgencyRowClass(urgentBatch)}`} index={index}>
      <div className="flex items-stretch">
        <div
          className="shrink-0 w-10 flex items-center justify-center text-term-text-muted hover:text-term-text cursor-pointer transition-colors text-sm"
          onClick={() => setExpanded((e) => !e)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setExpanded((x) => !x);
            }
          }}
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          aria-label="Expandir lotes"
        >
          <span className={`transition-transform duration-200 ${expanded ? "rotate-90" : ""}`}>
            ▸
          </span>
        </div>
        <div className="flex-1 min-w-0 py-3 pr-4">
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-term-text">{recipe.recipe_name}</span>
                <Badge value={recipe.recipe_type} />
              </div>
              <div className="flex items-center gap-3 mt-1 text-sm text-term-text-muted flex-wrap">
                <span>
                  <span className="text-term-text font-medium text-sm">
                    {recipe.total_servings_remaining}
                  </span>{" "}
                  porciones
                </span>
                <span>
                  {recipe.batch_count} lote{recipe.batch_count !== 1 ? "s" : ""}
                </span>
                {urgentBatch !== null && (
                  <span className={urgencyColor(urgentBatch)}>
                    {urgentBatch <= 0 ? "Vencido" : `vence en ${urgentBatch}d`}
                  </span>
                )}
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="shrink-0"
              onClick={() => setShowConsume((s) => !s)}
            >
              Consumir
            </Button>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-3">
          <BatchRows batches={recipe.batches} />
        </div>
      )}

      {showConsume && (
        <ConsumeForm
          batches={recipe.batches}
          recipeName={recipe.recipe_name}
          onClose={() => setShowConsume(false)}
          onDone={() => {
            setShowConsume(false);
            onConsumed();
          }}
        />
      )}
    </Card>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

type SortField = "name" | "servings" | "expiry";

export function StockPage({ focusRecipeId }: { focusRecipeId?: string } = {}) {
  const { dataVersion } = useDataVersion();
  const [sort, setSort] = useState<SortField>("name");

  const fetcher = useCallback(() => {
    void dataVersion;
    return stockApi.list();
  }, [dataVersion]);
  const { data, loading, error, refetch } = useApi(fetcher, [dataVersion]);

  if (loading) return <TopLoadingBar visible />;
  if (error) return <ErrorMessage message={error} onRetry={refetch} />;
  if (!data) return null;

  const totalServings = data.reduce((s, r) => s + r.total_servings_remaining, 0);
  const expiringCount = data.filter((r) =>
    r.batches.some((b) => {
      const d = daysUntil(b.expiry_date);
      return d !== null && d <= 3;
    }),
  ).length;

  const sorted = [...data].sort((a, b) => {
    if (sort === "servings") return b.total_servings_remaining - a.total_servings_remaining;
    if (sort === "expiry") {
      const aMin = a.batches.reduce<number>((min, bx) => {
        const d = daysUntil(bx.expiry_date);
        return d !== null ? Math.min(min, d) : min;
      }, Infinity);
      const bMin = b.batches.reduce<number>((min, bx) => {
        const d = daysUntil(bx.expiry_date);
        return d !== null ? Math.min(min, d) : min;
      }, Infinity);
      return aMin - bMin;
    }
    return a.recipe_name.localeCompare(b.recipe_name);
  });

  const hasAny = data.length > 0;

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto flex flex-col gap-6">
      <PageHeader
        title="Stock"
        icon={<StockIcon width={22} height={22} />}
        subtitle="Inventario de lotes disponibles"
      />

      {/* Summary bar */}
      <div className="flex gap-4 flex-wrap">
        {[
          { label: "Porciones totales", value: totalServings },
          { label: "Recetas", value: data.length },
          ...(expiringCount > 0
            ? [{ label: "Vencen pronto", value: expiringCount, urgent: true }]
            : []),
        ].map((s, i) => (
          <Card key={s.label} index={i} className="px-4 py-3 flex flex-col gap-0.5 min-w-28">
            <span
              className={`text-2xl font-semibold ${(s as { urgent?: boolean }).urgent ? "text-term-error" : "text-term-text"}`}
            >
              {s.value}
            </span>
            <span className="text-sm text-term-text-muted">{s.label}</span>
          </Card>
        ))}
      </div>

      {!hasAny ? (
        <EmptyState
          icon={<StockIcon strokeWidth={1.25} />}
          title="Sin stock"
          description="No hay lotes disponibles en este momento."
        />
      ) : (
        <>
          {/* Sort controls */}
          {data.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-term-text-muted">
              <span>Ordenar:</span>
              {(["name", "servings", "expiry"] as SortField[]).map((f) => (
                <button
                  key={f}
                  onClick={() => setSort(f)}
                  className={`px-2.5 py-1 rounded-full cursor-pointer transition-colors ${sort === f ? "bg-term-accent/10 text-term-accent" : "hover:bg-term-raised text-term-text-muted"}`}
                >
                  {f === "name" ? "Nombre" : f === "servings" ? "Porciones" : "Vencimiento"}
                </button>
              ))}
            </div>
          )}

          {/* Batch-tracked recipes */}
          <div className="flex flex-col gap-2">
            {sorted.map((r, i) => (
              <RecipeRow
                key={r.recipe_id}
                recipe={r}
                onConsumed={refetch}
                initialExpanded={r.recipe_id === focusRecipeId}
                index={i}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
